import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Input, SimpleLoader } from "senf-atomic-design-system";
import { useModals } from "../../molecules/modalStack/ModalProvider";

import { useSearchParams } from "react-router-dom";
import {
  auth,
  cloudFunctions,
  generateErrorMessage,
  generateLink,
  useSchemaValidation,
} from "senf-shared";

import {
  confirmPasswordReset,
  signInWithEmailAndPassword,
  verifyPasswordResetCode,
} from "firebase/auth";

import { httpsCallable } from "firebase/functions";
import { useFormik } from "formik";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import { AuthPageWrapper } from "./components/AuthPageWrapper";

const resetPassword = httpsCallable(cloudFunctions, "onPasswordReset");

const ResetForm = ({ formik }) => {
  const [loading, setLoading] = useState(false);
  const { openModal } = useModals();
  const { t } = useTranslation();
  const resendEmail = async () => {
    setLoading(true);
    resetPassword({
      email: formik.values.email,
      url: generateLink("mainApp", "resetPassword"),
    })
      .then(() => {
        openModal(null, {
          id: "resetEmailSent",
          title: t("global:emailSent"),
          description: t("auth:emailSentDescription"),
          cancelText: "OK",
        });
      })
      .catch((e) => {
        openModal(null, {
          id: "resetEmailError",
          title: t(generateErrorMessage(e.message)),
          description: t("auth:tryAgainLater"),
          cancelText: "OK",
        });
      })
      .finally(() => setLoading(false));
  };
  return (
    <>
      <Input
        name="email"
        type="email"
        disabled={loading}
        value={formik.values.email}
        onChange={formik.handleChange}
        placeholder={t("auth:email")}
      />
      <Button
        onClick={resendEmail}
        variant="primary"
        text={t("global:requestAgain")}
        classNameWrapper="w-full"
        className="w-full"
        size="xl"
        loading={loading}
        disabled={loading}
        disabledNotice={`${t("util:isBeingSent", { val: t("auth:email") })}...`}
      />
    </>
  );
};
export const ResetPassword = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get("oobCode");
  const continueUrl = searchParams.get("continueUrl") || "/";
  const { login } = useSchemaValidation();
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: login,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  const verifyCode = async () => {
    try {
      const email = await verifyPasswordResetCode(auth, oobCode || "");
      formik.setFieldValue("email", email);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    verifyCode();
  }, []);
  useEffect(() => {
    if (error) setError(true);
  }, [error]);

  const submit = async () => {
    if (!oobCode) return;
    setLoading(true);
    try {
      if (!formik.values.email || !formik.values.password) return;
      await confirmPasswordReset(auth, oobCode, formik.values.password);
      await signInWithEmailAndPassword(
        auth,
        formik.values.email,
        formik.values.password,
      );
      window.location.href = decodeURIComponent(continueUrl);
    } catch (error) {
      setError(true);
      console.log(error);
    }
  };
  if (loading) return <SimpleLoader />;
  return (
    <AuthPageWrapper
      message={{
        error,
        text: error
          ? "Der angeforderte Link ist nicht mehr gültig."
          : undefined,
        children: <ResetForm formik={formik} />,
      }}
    >
      <h2 className="heading-xl text-center [text-wrap:balance]">{`You have requested to reset your password`}</h2>
      <div className="flex w-full flex-col items-center gap-2">
        <Input
          name="email"
          type="email"
          value={formik.values.email}
          disabled
          placeholder={t("auth:email")}
        />
        <Input
          name="password"
          type="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          placeholder={t("auth:password")}
        />
        <Button
          variant="primary"
          size="lg"
          onClick={submit}
          text={t("global:next")}
          className="w-full"
          classNameWrapper="w-full"
          loading={loading}
          disabled={loading}
        />
      </div>
    </AuthPageWrapper>
  );
};
